import { computed, ref } from 'vue'
import axios from 'axios'

// Post-trip bus walkthrough (functions handlers/busWalkthrough.ts): after a trip ends, the phone that streamed it scans
// the last-seat sticker within CHECK_WINDOW_MIN of the end, or declares why it can't (SkipReason). The server validates the window
// with `scannedAt` (device time), so a scan saved offline and sent later still counts.

/** The sticker already installed in every bus (one code for all). */
export const GENERAL_SECURITY_CODE = 'rs_security_check'
/** A unique sticker: bound to its bus by its first scan (the streamer confirms or corrects the bus). */
const UNIQUE_SECURITY_CODE = /^rs_check_[A-Za-z0-9]{8,}$/
/**
 * The two ways to skip the scan (same as the server): the bus has no sticker that reads (with its plate) or the phone
 * can't scan. Both are declared by typing ACCEPT_WORD under the statement.
 */
export type SkipReason = 'noSticker' | 'camera'
export const ACCEPT_WORD = 'revisado'
const PLATE_MIN = 5
/** Plate as the server compares it: letters and digits, upper case. */
export const normalizePlate = (value: string): string => value.normalize('NFD').replace(/[^A-Za-z0-9]/g, '').toUpperCase()
export const isPlate = (value: string): boolean => normalizePlate(value).length >= PLATE_MIN
/** The typed declaration: ACCEPT_WORD whatever its case, accents or spaces. */
export const isAccepted = (value: string): boolean =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase() === ACCEPT_WORD
/** The banner and the modal turn red for the last minutes of the window. */
export const URGENT_MS = 2 * 60 * 1000
/** Same as the server constant: only used for the countdown until the pending row is read. */
export const CHECK_WINDOW_MIN = 10

/** A code the check endpoint accepts. */
export const isWalkthroughCode = (text: string | null | undefined): boolean =>
  !!text && (text === GENERAL_SECURITY_CODE || UNIQUE_SECURITY_CODE.test(text))

/** Any walkthrough sticker, well-formed or not: the passenger QR reader (`rs_<tag>`) must never take one as a tag. */
export const isBusWalkthroughSticker = (text: string | null | undefined): boolean =>
  !!text && (text === GENERAL_SECURITY_CODE || text.startsWith('rs_check_'))

export interface BusWalkthroughPayload {
  tripID: string
  /** `correctVehicle`: "Corregir" on the done screen moves the sticker to another bus of the fleet. */
  action: 'scan' | 'skip' | 'correctVehicle'
  code?: string
  lat?: number | null
  lng?: number | null
  accuracy?: number | null
  scannedAt: string
  deviceID?: string | null
  /** This copy confirms the bus of a sticker's first scan (the server asks with 409 confirmVehicle). */
  bind?: boolean
  /** The bus the streamer confirmed or picked (a first scan, or a correction). */
  vehicleID?: string
  reason?: SkipReason
  plate?: string
  accept?: string
}

/** One of the customer's live vehicles: the server sends the list; the app never decides the fleet. */
export interface FleetVehicle { id: string, name: string | null }

export type BusWalkthroughResult =
  /** For a unique sticker, the bus it is on and the fleet to correct it with. */
  | { kind: 'ok', status: 'done' | 'skipped', vehicle: FleetVehicle | null, vehicles: FleetVehicle[] }
  /** The sticker's first scan: which bus is it on? The proposal (null: none fits) and the fleet. */
  | { kind: 'confirmVehicle', vehicle: FleetVehicle | null, vehicles: FleetVehicle[] }
  | { kind: 'rejected', httpStatus: number, error: string, distanceM?: number | null }
  | { kind: 'offline' }

const fleetOf = (data: any): FleetVehicle[] => (Array.isArray(data?.vehicles) ? data.vehicles : [])

/** Every status comes back as a value (no throw), and no global retry: the queue retries instead. */
export const WALKTHROUGH_REQUEST_OPTIONS = {
  timeout: 20000,
  validateStatus: () => true,
  'axios-retry': { retries: 0 },
} as any

export async function postBusWalkthrough(payload: BusWalkthroughPayload): Promise<BusWalkthroughResult> {
  try {
    const res = await axios.post('busWalkthrough', payload, WALKTHROUGH_REQUEST_OPTIONS)
    const data = res.data || {}
    if (res.status === 200 && data.ok) {
      return { kind: 'ok', status: data.status === 'skipped' ? 'skipped' : 'done', vehicle: data.vehicle ?? null, vehicles: fleetOf(data) }
    }
    if (res.status === 409 && data.error === 'confirmVehicle') return { kind: 'confirmVehicle', vehicle: data.vehicle ?? null, vehicles: fleetOf(data) }
    if (res.status >= 500 || res.status === 0) return { kind: 'offline' }
    return { kind: 'rejected', httpStatus: res.status, error: data.error || 'badRequest', distanceM: data.distanceM ?? null }
  } catch {
    // Never reached the server (no signal, timeout): the caller queues it.
    return { kind: 'offline' }
  }
}

export interface CheckPosition { lat: number, lng: number, accuracy: number | null }

/**
 * Where the phone is for the radius check: the tracking plugin's fix (not persisted, so no point is uploaded after the
 * trip ended), else the browser's. null after ~15 s without a fix; the scan is still sent.
 */
export async function getCheckPosition(): Promise<CheckPosition | null> {
  const bg = (window as any).BackgroundGeolocation
  if (bg?.getCurrentPosition) {
    try {
      const location: any = await bg.getCurrentPosition({ timeout: 15, maximumAge: 15000, samples: 2, persist: false, desiredAccuracy: 10 })
      const c = location?.coords
      if (c && Number.isFinite(c.latitude)) return { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy ?? null }
    } catch { /* fall through to the browser */ }
  }
  if (!navigator.geolocation) return null
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 15000 },
    )
  })
}

/** A circle where the scan counts (server `checkZones`: the trip's last point, the block hub's arrival zone). */
export interface CheckZone { lat: number, lng: number, radiusM: number }

export interface PendingBusWalkthrough {
  id: string
  tripID: string
  dueAt: string
  routeName?: string | null
  vehicleName?: string | null
  zones?: CheckZone[]
}

/** The caller's pending checks still inside their window; null when the server didn't answer (offline). */
export async function readPendingChecks(): Promise<PendingBusWalkthrough[] | null> {
  try {
    const res = await axios.get('busWalkthrough/pending', WALKTHROUGH_REQUEST_OPTIONS)
    return res.status === 200 && Array.isArray(res.data?.checks) ? res.data.checks : null
  } catch {
    return null
  }
}

/**
 * At trip end the server has already decided: the close voids a false start, then creates the check, and only then
 * stops the stream the app is watching. One read tells whether this trip has a check: the check, null (none: a voided
 * false start), or 'offline' (can't tell; the caller opens it anyway, kept on the phone).
 */
export async function checkAtTripEnd(tripID: string): Promise<PendingBusWalkthrough | null | 'offline'> {
  const checks = await readPendingChecks()
  if (!checks) return 'offline'
  return checks.find(check => check.tripID === tripID) ?? null
}

/** The caller's pending checks still inside their window; [] when offline. */
export async function fetchPendingWalkthroughs(): Promise<PendingBusWalkthrough[]> {
  return (await readPendingChecks()) ?? []
}

// --- The checks the top banner shows (components/BusWalkthroughBanner.vue), on every screen, with the time left. ---

/** Past every zone by this much, beyond the GPS accuracy, the phone has left the bus: the check is lost. */
const LEAVE_MARGIN_M = 150
/** A fix less precise than this can't tell. */
const LEAVE_MAX_ACCURACY_M = 100
const LEAVE_POLL_MS = 30_000
/** A lost check shows as lost this long, then the banner lets go. */
const LOST_SHOWN_MS = 60_000
const LOST_KEY = 'busWalkthroughLost'
/** The open checks, kept on the phone: a killed app, even offline, reopens them on the next start. */
const KNOWN_KEY = 'busWalkthroughKnown'

export interface ShownCheck extends PendingBusWalkthrough {
  queued: boolean
  lost: boolean
}

interface LostCheck { dueAt: string, lostAt: number }

const metersBetween = (a: { lat: number, lng: number }, b: { lat: number, lng: number }) => {
  const rad = Math.PI / 180
  const s1 = Math.sin((b.lat - a.lat) * rad / 2)
  const s2 = Math.sin((b.lng - a.lng) * rad / 2)
  const h = s1 * s1 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * s2 * s2
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h))
}

/** Clearly out of every zone (accuracy included); a check with no zone, or a vague fix, never counts as left. */
export function hasLeft(position: CheckPosition, zones: CheckZone[] | undefined): boolean {
  if (!zones?.length || position.accuracy == null || position.accuracy > LEAVE_MAX_ACCURACY_M) return false
  return zones.every(zone => metersBetween(position, zone) - position.accuracy! > zone.radiusM + LEAVE_MARGIN_M)
}

function readLost(): Record<string, LostCheck> {
  try {
    return JSON.parse(localStorage.getItem(LOST_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

/** Trips whose check was lost (the phone left the bus); kept until their window ends, so they never come back. */
export const lostChecks = ref<Record<string, LostCheck>>(readLost())

function writeLost(lost: Record<string, LostCheck>) {
  lostChecks.value = lost
  try {
    localStorage.setItem(LOST_KEY, JSON.stringify(lost))
  } catch { /* storage full or blocked: lost for this session only */ }
}

type KnownCheck = PendingBusWalkthrough

function readKnown(): KnownCheck[] {
  try {
    const list = JSON.parse(localStorage.getItem(KNOWN_KEY) || '[]')
    return Array.isArray(list) ? list.filter(check => check?.tripID && Date.parse(check.dueAt) > Date.now()) : []
  } catch {
    return []
  }
}

/** The open checks: the server's when it answers, the phone's copy while offline. Kept in localStorage. */
const knownChecks = ref<KnownCheck[]>(readKnown())
export const nowMs = ref(Date.now())

function setKnown(list: KnownCheck[]) {
  knownChecks.value = list
  try {
    localStorage.setItem(KNOWN_KEY, JSON.stringify(list))
  } catch { /* storage full or blocked: kept for this session only */ }
  syncWatch()
}

export const shownChecks = computed<ShownCheck[]>(() => {
  const queued = new Set(queuedTripIDs())
  void busWalkthroughVersion.value // re-read the queue when it changes
  return knownChecks.value
    .filter(check => Date.parse(check.dueAt) > nowMs.value)
    .filter(check => { const lost = lostChecks.value[check.tripID]; return !lost || nowMs.value - lost.lostAt < LOST_SHOWN_MS })
    .map(check => ({ ...check, queued: queued.has(check.tripID), lost: !!lostChecks.value[check.tripID] }))
})

/** App open, resume, reconnect and every change: the caller's pending checks from the server. Offline keeps what was. */
export async function refreshPendingChecks() {
  const server = await readPendingChecks()
  const now = Date.now()
  writeLost(Object.fromEntries(Object.entries(lostChecks.value).filter(([, lost]) => Date.parse(lost.dueAt) > now)))
  if (!server) {
    // Offline: what the phone knows, minus what ran out of time.
    setKnown(knownChecks.value.filter(check => Date.parse(check.dueAt) > now))
    return
  }
  // The server is the truth: its row exists before the phone hears the trip ended, so a check it doesn't list is
  // answered, voided or out of time, never "not written yet".
  setKnown(server)
}

/** A check open on the phone (opened at trip end, or read from the server): kept until answered, lost or out of time. */
export function keepCheckShown(check: PendingBusWalkthrough) {
  setKnown([...knownChecks.value.filter(c => c.tripID !== check.tripID), check])
}

/** The server has no check for the trip (a voided false start, or the flag is off): drop it so it never reopens. */
export function forgetCheck(tripID: string) {
  setKnown(knownChecks.value.filter(check => check.tripID !== tripID))
}

export function markLost(check: { tripID: string, dueAt: string }) {
  writeLost({ ...lostChecks.value, [check.tripID]: { dueAt: check.dueAt, lostAt: Date.now() } })
}

// While a check is open: a 1 s clock for the countdowns, and the phone's position every LEAVE_POLL_MS.
let clock: number | null = null
let poll: number | null = null
let polling = false
const openChecks = () => shownChecks.value.filter(check => !check.queued && !check.lost)

/**
 * The check is mandatory: on app start, resume and reconnect, a check still open opens by itself (the modal can't be
 * closed while it can be done). Nothing is opened over the one already showing.
 */
export function promptOpenCheck() {
  if (activeBusWalkthrough.value) return
  const check = openChecks().sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt))[0]
  if (check) void openBusWalkthrough({ tripID: check.tripID, dueAt: check.dueAt, routeName: check.routeName, vehicleName: check.vehicleName })
}

async function checkDistance() {
  const watched = openChecks().filter(check => check.zones?.length)
  if (polling || !watched.length) return
  polling = true
  try {
    const position = await getCheckPosition()
    if (position) watched.filter(check => hasLeft(position, check.zones)).forEach(markLost)
  } finally {
    polling = false
  }
}

function syncWatch() {
  nowMs.value = Date.now()
  const active = knownChecks.value.some(check => Date.parse(check.dueAt) > nowMs.value)
  if (active && clock == null) {
    clock = window.setInterval(() => {
      nowMs.value = Date.now()
      if (!knownChecks.value.some(check => Date.parse(check.dueAt) > nowMs.value)) syncWatch()
    }, 1000)
    poll = window.setInterval(() => { void checkDistance() }, LEAVE_POLL_MS)
  } else if (!active && clock != null) {
    window.clearInterval(clock)
    if (poll != null) window.clearInterval(poll)
    clock = poll = null
  }
}

// --- Offline queue: one payload per trip in localStorage, sent on reconnect, app resume and next open. ---

const QUEUE_KEY = 'busWalkthroughQueue'
/** A pending row the server hasn't created yet (404 noCheck) is retried this many times before it is dropped. */
const MAX_NO_CHECK_ATTEMPTS = 5

interface QueuedCheck { payload: BusWalkthroughPayload, attempts: number }

/** Bumped when the queue or the open check changes, so the home banner reloads. */
export const busWalkthroughVersion = ref(0)

function readQueue(): Record<string, QueuedCheck> {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

function writeQueue(queue: Record<string, QueuedCheck>) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  } catch { /* storage full or blocked: nothing else to do */ }
  busWalkthroughVersion.value++
}

export function queueBusWalkthrough(payload: BusWalkthroughPayload) {
  const queue = readQueue()
  queue[payload.tripID] = { payload, attempts: 0 }
  writeQueue(queue)
}

export function queuedTripIDs(): string[] {
  return Object.keys(readQueue())
}

let flushing = false
/** Sends what is queued. A sticker's first scan can't confirm its bus in the background: the check opens to ask. */
export async function flushBusWalkthroughQueue() {
  if (flushing) return
  flushing = true
  try {
    for (const [tripID, item] of Object.entries(readQueue())) {
      const result = await postBusWalkthrough(item.payload)
      if (result.kind === 'offline') break
      const queue = readQueue()
      if (result.kind === 'rejected' && result.error === 'noCheck' && item.attempts + 1 < MAX_NO_CHECK_ATTEMPTS) {
        queue[tripID] = { ...item, attempts: item.attempts + 1 }
      } else {
        delete queue[tripID]
      }
      writeQueue(queue)
      if (result.kind === 'confirmVehicle') {
        void openBusWalkthrough({ tripID, payload: item.payload, confirm: { vehicle: result.vehicle, vehicles: result.vehicles } })
      }
    }
  } finally {
    flushing = false
  }
}

// --- The one open check (components/BusWalkthroughHost.vue renders it): stream end, push tap and home banner. ---

export interface BusWalkthroughRequest {
  tripID: string
  dueAt?: string | null
  routeName?: string | null
  vehicleName?: string | null
  /** A queued first scan of a sticker: the modal asks which bus it is on, then resends `payload` with it. */
  payload?: BusWalkthroughPayload
  confirm?: { vehicle: FleetVehicle | null, vehicles: FleetVehicle[] }
}

export const activeBusWalkthrough = ref<BusWalkthroughRequest | null>(null)
let waiters: Array<() => void> = []

/** Opens the check for a trip and resolves when it closes. The trip already open is not opened twice. */
export function openBusWalkthrough(request: BusWalkthroughRequest): Promise<void> {
  const current = activeBusWalkthrough.value
  if (!current || current.tripID !== request.tripID) {
    if (current) resolveWaiters()
    activeBusWalkthrough.value = request
  }
  return new Promise(resolve => { waiters.push(resolve) })
}

export function closeBusWalkthrough() {
  activeBusWalkthrough.value = null
  busWalkthroughVersion.value++
  resolveWaiters()
}

function resolveWaiters() {
  const pending = waiters
  waiters = []
  pending.forEach(resolve => resolve())
}

/** Push `data: {type: 'busWalkthrough', tripID}` (trip-end hook): opens the check, tapped or in the foreground. */
export function openBusWalkthroughFromPush(message: any): boolean {
  if (message?.type !== 'busWalkthrough' || !message?.tripID) return false
  void openBusWalkthrough({ tripID: String(message.tripID) })
  return true
}
